import { useState } from "react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { useAdminGuard } from "@/hooks/useAdminGuard";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useAuthUser } from "@/App";

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const user = useAuthUser();
  const { isAdmin } = useAdminGuard();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("admin-sidebar-collapsed") === "true";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("admin-sidebar-collapsed", String(next));
      } catch {}
      return next;
    });
  };

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
      </div>
    );
  }

  const sidebarWidth = isCollapsed ? "w-[60px]" : "w-[260px]";
  const contentPadding = isCollapsed ? "md:pl-[60px]" : "md:pl-[260px]";

  return (
    <div className="min-h-screen bg-[#0F172A] font-sans">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 hidden md:block transition-all duration-300 ease-in-out",
          sidebarWidth
        )}
      >
        <AdminSidebar isCollapsed={isCollapsed} onToggle={toggleCollapsed} />
      </aside>

      <div
        className={cn(
          contentPadding,
          "flex flex-col min-h-screen transition-all duration-300 ease-in-out"
        )}
      >
        <main className="flex-1 min-w-0 p-4 md:p-8 animate-in fade-in duration-500">
          {children}
        </main>
      </div>
    </div>
  );
}
